/**
 * Footprint geometry shared by every wall edit: rotated-rectangle AABBs and
 * polygons for fixtures and walls, meaningful-overlap tests (a few mm of
 * contact is not an overlap), and the post-edit fixture-overlap pass.
 *
 * Split out of `wallStretch.js` so any op that moves geometry reports the same
 * overlaps rather than growing its own copy.
 *
 * No Firestore, no React.
 */

import { apply3DRotationTo2D } from './geometryUtils';
import { wallEndpointsXZ } from './wallDisplacement';

/**
 * Axis-aligned bounding box (in the XZ plane) for a fixture, accounting
 * for the rotation around Y. For axis-aligned fixtures (rotation = 0,
 * π/2, π, 3π/2) this matches dimensions exactly; for rotated fixtures
 * it's the smallest AABB covering the rotated rectangle.
 */
export function fixtureAABB(fixture) {
  if (!fixture || !fixture.center || !fixture.dimensions) return null;
  const halfX = (fixture.dimensions.x || 0) / 2;
  const halfZ = (fixture.dimensions.z || 0) / 2;
  const ry = fixture.rotation?.y || 0;
  const cosR = Math.abs(Math.cos(ry));
  const sinR = Math.abs(Math.sin(ry));
  const halfXAABB = halfX * cosR + halfZ * sinR;
  const halfZAABB = halfX * sinR + halfZ * cosR;
  return {
    minX: fixture.center.x - halfXAABB,
    maxX: fixture.center.x + halfXAABB,
    minZ: fixture.center.z - halfZAABB,
    maxZ: fixture.center.z + halfZAABB,
  };
}

// Rotated rectangle (4 corners in world XZ) for a fixture. Matches the
// orientation drawObject uses on screen — local axes swapped so x runs
// along depth (dim.z) and y along width (dim.x), and the angle comes
// from apply3DRotationTo2D + atan2 (proper Y-axis handedness). This is
// the truthful collision shape; AABB is too generous for rotated
// fixtures and produces false-positive overlap warnings.
export function fixturePolygon(fixture) {
  if (!fixture || !fixture.center || !fixture.dimensions) return null;
  const dir = apply3DRotationTo2D(fixture.rotation, [0, 0, 1]);
  const ang = Math.atan2(dir[1], dir[0]);
  const cosR = Math.cos(ang);
  const sinR = Math.sin(ang);
  const hd = (fixture.dimensions.z || 0) / 2;
  const hw = (fixture.dimensions.x || 0) / 2;
  const local = [
    { x: -hd, y: -hw },
    { x: hd, y: -hw },
    { x: hd, y: hw },
    { x: -hd, y: hw },
  ];
  return local.map((p) => ({
    x: fixture.center.x + p.x * cosR - p.y * sinR,
    z: fixture.center.z + p.x * sinR + p.y * cosR,
  }));
}

// Wall footprint as a 4-corner polygon: centerline endpoints expanded
// by thickness/2 along the wall's perpendicular. Exact for any
// orientation, unlike wallFootprintAABB which inflates diagonals.
export function wallPolygon(wall) {
  if (!wall || !wall.center || !wall.dimensions) return null;
  const ends = wallEndpointsXZ(wall);
  if (!ends) return null;
  return segmentPolygon(ends.a, ends.b, wall.dimensions.z || 0.1);
}

/**
 * The footprint of a straight run from `p1` to `p2`, `thickness` wide, as a
 * 4-corner polygon — exact at any angle. `segmentAABB` is its box.
 */
export function segmentPolygon(p1, p2, thickness) {
  const half = (thickness || 0) / 2;
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-9) return null;
  const px = (-dz / len) * half;
  const pz = (dx / len) * half;
  return [
    { x: p1.x + px, z: p1.z + pz },
    { x: p2.x + px, z: p2.z + pz },
    { x: p2.x - px, z: p2.z - pz },
    { x: p1.x - px, z: p1.z - pz },
  ];
}

// SAT polygon-overlap with a tolerance (positive ⇒ require at least
// `tol` of overlap on every separating-axis projection). Mirrors the
// shared-lib doPolygonsOverlap implementation but kept local to
// avoid dragging the bathroom-analyzer module into wallStretch's
// dependency graph.
export function polygonsOverlapMeaningful(poly1, poly2, tol = CONTACT_TOL) {
  const project = (poly, axis) => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of poly) {
      const v = p.x * axis.x + p.z * axis.z;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { min, max };
  };
  const test = (poly) => {
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      const ex = poly[j].x - poly[i].x;
      const ez = poly[j].z - poly[i].z;
      const len = Math.sqrt(ex * ex + ez * ez);
      if (len < 1e-9) continue;
      const axis = { x: -ez / len, z: ex / len };
      const a = project(poly1, axis);
      const b = project(poly2, axis);
      if (a.max < b.min + tol || b.max < a.min + tol) return false;
    }
    return true;
  };
  return test(poly1) && test(poly2);
}

/**
 * AABB for a wall-segment with a given thickness.
 */
export function segmentAABB(p1, p2, thickness) {
  const halfT = (thickness || 0) / 2;
  return {
    minX: Math.min(p1.x, p2.x) - halfT,
    maxX: Math.max(p1.x, p2.x) + halfT,
    minZ: Math.min(p1.z, p2.z) - halfT,
    maxZ: Math.max(p1.z, p2.z) + halfT,
  };
}

// Meaningful overlap — requires at least `tol` of overlap on each
// axis. Filters out sub-mm grazing from floating-point AABB
// computation (e.g. a fixture's AABB ending exactly at a wall's
// thickness edge often shows up as a ~10⁻⁷ m overlap, which isn't
// real contact).
export const CONTACT_TOL = 0.005; // 5 mm
export function aabbsOverlapMeaningful(a, b, tol = CONTACT_TOL) {
  return (
    a.minX < b.maxX - tol &&
    a.maxX > b.minX + tol &&
    a.minZ < b.maxZ - tol &&
    a.maxZ > b.minZ + tol
  );
}

/**
 * AABB of a wall's footprint (centerline endpoints expanded by
 * thickness/2). Exact for axis-aligned walls; conservative (slightly
 * larger than the true footprint) for diagonal walls.
 */
export function wallFootprintAABB(wall) {
  if (!wall || !wall.center || !wall.dimensions) return null;
  const ends = wallEndpointsXZ(wall);
  const t = wall.dimensions.z || 0.1;
  return segmentAABB(ends.a, ends.b, t);
}

/**
 * Post-edit overlap detection (non-blocking).
 *
 * Flag any cascaded child (door / window / opening / object) that *moved*
 * and then ran into a non-parent wall. The entry's `kind` rides along so the
 * UI can say "A door would overlap…" / "A bed would overlap…" rather than
 * the generic "A fixture would overlap…".
 *
 * BEFORE and AFTER footprints are compared so pre-existing overlaps stay
 * quiet — children already sitting on a wall (wall-mounted accessories with
 * no proper parent link) and AABBs grazing a wall by a few mm. One warning
 * per child id; first wall hit wins.
 *
 * @returns {Array} `fixture-overlap` warning entries, ready to push onto a
 *   plan's `errors`.
 */
export function detectFixtureOverlaps(
  structureData,
  { affectedItems, affectedStructureWallsById }
) {
  const out = [];
  if (!affectedItems || affectedItems.length === 0) return out;

  const roomWallToStructureWallId = new Map();
  for (const room of structureData.rooms || []) {
    for (const rw of room.walls || []) {
      if (rw?.id && rw.alignment?.structureWallId) {
        roomWallToStructureWallId.set(rw.id, rw.alignment.structureWallId);
      }
    }
  }

  const reported = new Set();
  for (const item of affectedItems) {
    if (reported.has(item.id)) continue;
    if (!item.before?.center || !item.after?.center) continue;
    // Cheap AABB pre-filter, then exact SAT on the rotated rectangle for
    // both fixture and wall — AABB alone over-reports for rotated
    // tables/beds whose inflated bounding box clips a wall the actual
    // rectangle never touches.
    const beforeFixBox = fixtureAABB(item.before);
    const afterFixBox = fixtureAABB(item.after);
    if (!beforeFixBox || !afterFixBox) continue;
    const beforeFixPoly = fixturePolygon(item.before);
    const afterFixPoly = fixturePolygon(item.after);
    if (!beforeFixPoly || !afterFixPoly) continue;
    const pid =
      item.parentWallIds?.[0] || item.after.parentWallIds?.[0] || null;
    const parentStructureWallId = pid
      ? roomWallToStructureWallId.get(pid) || pid
      : null;
    for (const wall of structureData.walls || []) {
      if (!wall || wall.id === parentStructureWallId) continue;
      if (!wall.center || !wall.dimensions) continue;
      const wallAfter = affectedStructureWallsById?.get(wall.id)?.after || wall;
      const wallAfterBox = wallFootprintAABB(wallAfter);
      if (!wallAfterBox) continue;
      if (!aabbsOverlapMeaningful(afterFixBox, wallAfterBox)) continue;
      const wallAfterPoly = wallPolygon(wallAfter);
      if (!wallAfterPoly) continue;
      if (!polygonsOverlapMeaningful(afterFixPoly, wallAfterPoly)) continue;
      const wallBeforePoly = wallPolygon(wall);
      if (
        wallBeforePoly &&
        polygonsOverlapMeaningful(beforeFixPoly, wallBeforePoly)
      ) {
        continue;
      }
      // For furniture the category schema is `{ bed: {} }`, `{ toilet: {} }`
      // — one key per object — so the panel can name it.
      const categoryKey =
        item.kind === 'object'
          ? Object.keys(item.before?.category || {})[0] || null
          : null;
      out.push({
        code: 'fixture-overlap',
        severity: 'warning',
        ref: {
          fixtureId: item.id,
          fixtureKind: item.kind,
          fixtureCategory: categoryKey,
          roomId: item.roomId || null,
          wallId: wall.id,
        },
      });
      reported.add(item.id);
      break;
    }
  }
  return out;
}
